import { ChartOptions } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { FormatToShortPriceTemplate } from '../../../../../utils/FormatValue';
import { mockOpportunityRouteData } from '@pages/Opportunity-dashboard/opportunityTransactionsMock';
import { IOpportunityRouteData, ITransactionOpportunity } from '@sp-types/transactionOpportunityType';
import { OpLegendSection } from './OpRouteDistribution';
import React from 'react';

interface Props {
  data: ITransactionOpportunity[];
}

export const RoutesProfitBar: React.FC<Props> = ({ data }) => {
  return (
    <div className="flex flex-col flex-1 gap-3">
      <p className="text-lg font-medium">Routes distribution</p>
      <div className="flex flex-1 flex-row gap-4">
        <div className="h-full flex flex-1 flex-row  ">
          <ProfitLossChart data={getProfitLossChartData(data, mockOpportunityRouteData)} />
        </div>
        <OpLegendSection
          data={getProfitLossChartData(data, mockOpportunityRouteData).map(({ color, name, value }) => ({
            color,
            fieldName: name,
            value,
          }))}
        />
      </div>
    </div>
  );
};

interface ProfitLossChartProps {
  data: {
    color: string;
    value: number;
    name: string;
  }[];
}

const ProfitLossChart: React.FC<ProfitLossChartProps> = ({ data }) => {
  const chartData = {
    labels: data.map((item) => item.name),
    datasets: [
      {
        label: 'Profit',
        data: data.map((item) => item.value),
        backgroundColor: data.map((item) => item.color),
        barThickness: 20,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    scales: {
      x: {
        ticks: { display: false },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true, // Disable y-axis grid lines
        },
        ticks: {
          stepSize: 500_000,
          callback: (value: string | number) => {
            if (typeof value === 'number') {
              return FormatToShortPriceTemplate(value, '', 0);
            } else {
              const parsedValue = parseFloat(value);
              return FormatToShortPriceTemplate(parsedValue);
            }
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        font: { weight: '200' },
        padding: 0,
        position: 'left',
        text: 'P/L',
        display: true,
      },
    },
  };

  return <Bar data={chartData} options={options} />;
};

const getProfitLossChartData = (
  data: ITransactionOpportunity[],
  opportunityRouteDataDict: Record<string, IOpportunityRouteData>,
): {
  color: string;
  value: number;
  name: string;
}[] => {
  return Object.values(
    data.reduce((acc, { quantity, routesOptionData }) => {
      const { routeId, buyingPrice, sellingPrice } = routesOptionData[0];
      const routeProfit = (sellingPrice - buyingPrice) * quantity;

      if (!acc[routeId]) {
        acc[routeId] = {
          color: opportunityRouteDataDict[routeId].color,
          value: 0,
          name: opportunityRouteDataDict[routeId].name,
        };
      }

      acc[routeId].value += routeProfit;

      return acc;
    }, {} as Record<string, { color: string; value: number; name: string }>),
  );
};
